// Disposition responsive et thème du cockpit — fonctions PURES (testées dans dashboard.test.ts).
//
//  - Bureau : 12 colonnes. Tablette : 8. Mobile : 4 puis 1. La disposition ENREGISTRÉE est celle des 12 colonnes ; sur les écrans
//    plus étroits la grille se réorganise toute seule (aucun widget perdu) et la modification est désactivée : on n'enregistre
//    jamais une disposition recalculée pour un petit écran par-dessus celle du poste de travail.
//  - Thème : clair | sombre | système, propre au cockpit (variables CSS Amiral, redéfinies pour le clair) ; les graphiques le suivent.

export const DESKTOP_COLUMNS = 12
export const BREAKPOINTS: { w: number; c: number }[] = [{ w: 520, c: 1 }, { w: 760, c: 4 }, { w: 1080, c: 8 }]

/** Nombre de colonnes pour une largeur de conteneur en pixels. */
export function columnsFor(width: number): number {
  for (const b of BREAKPOINTS) if (width < b.w) return b.c
  return DESKTOP_COLUMNS
}
export const canEditAt = (columns: number) => columns === DESKTOP_COLUMNS

interface Cell { id: string; x: number; y: number; w: number; h: number }

/**
 * Réorganise la disposition de référence (12 colonnes) pour une grille plus étroite : AUCUN widget perdu, aucun chevauchement,
 * ordre de lecture conservé (haut → bas, gauche → droite). Calcul déterministe — la disposition enregistrée n'est jamais modifiée.
 *  - 1 colonne : une pile verticale ; 4 ou 8 colonnes : largeurs proportionnelles (au moins 1), puis première place libre.
 */
export function reflow<T extends Cell>(items: T[], columns: number): T[] {
  if (columns >= DESKTOP_COLUMNS) return items.map(i => ({ ...i }))
  const order = [...items].sort((a, b) => a.y - b.y || a.x - b.x)
  const taken: boolean[][] = []
  const free = (x: number, y: number, w: number, h: number) => { for (let r = y; r < y + h; r++) for (let c = x; c < x + w; c++) if (taken[r]?.[c]) return false; return true }
  const mark = (x: number, y: number, w: number, h: number) => { for (let r = y; r < y + h; r++) { taken[r] ??= []; for (let c = x; c < x + w; c++) taken[r][c] = true } }
  const placed = new Map<string, T>()
  for (const it of order) {
    const w = columns === 1 ? 1 : Math.max(1, Math.min(columns, Math.round((it.w * columns) / DESKTOP_COLUMNS)))
    const wantX = columns === 1 ? 0 : Math.min(columns - w, Math.round((it.x * columns) / DESKTOP_COLUMNS))
    let done = false
    for (let y = 0; !done; y++) {
      for (const x of [wantX, ...Array.from({ length: columns - w + 1 }, (_, k) => k)]) {
        if (free(x, y, w, it.h)) { mark(x, y, w, it.h); placed.set(it.id, { ...it, x, y, w }); done = true; break }
      }
    }
  }
  return items.map(i => placed.get(i.id)!)
}

export type ThemeChoice = 'light' | 'dark' | 'system'
export const THEME_CHOICES: ThemeChoice[] = ['light', 'dark', 'system']
export const parseTheme = (v: unknown): ThemeChoice => (v === 'light' || v === 'dark' || v === 'system' ? v : 'system')
/** Thème effectivement affiché : « système » suit la préférence du navigateur. */
export const resolveTheme = (choice: ThemeChoice, systemPrefersDark: boolean): 'light' | 'dark' => (choice === 'system' ? (systemPrefersDark ? 'dark' : 'light') : choice)

/** Couleurs des graphiques, lues dans les variables CSS du cockpit : une seule source, le même rendu clair ou sombre. */
export interface ChartColors { text: string; muted: string; grid: string; tooltipBg: string; series: string[] }
export const CHART_VARS: Record<'text' | 'muted' | 'grid' | 'tooltipBg', string> = { text: '--dash-text', muted: '--dash-muted', grid: '--dash-grid', tooltipBg: '--dash-tooltip' }
export const SERIES_VARS = ['--dash-s1', '--dash-s2', '--dash-s3', '--dash-s4', '--dash-s5', '--dash-s6']
export const FALLBACK_COLORS: Record<'light' | 'dark', ChartColors> = {
  dark: { text: '#e2e8f0', muted: '#94a3b8', grid: 'rgba(255,255,255,0.08)', tooltipBg: '#0f172a', series: ['#4f7cff', '#2dd4bf', '#f5a623', '#ff6b5b', '#a78bfa', '#00d4ff'] },
  light: { text: '#0f172a', muted: '#475569', grid: 'rgba(15,23,42,0.1)', tooltipBg: '#ffffff', series: ['#2a4dc9', '#0f9f8f', '#c27a07', '#d9473a', '#7c3aed', '#0891b2'] },
}
